import React, { useEffect, useState } from "react";
import qs from "query-string";
import gql from "graphql-tag";
import { useMutation } from "@apollo/client";

import GoldSpinner from "../../assets/Spinner/GoldSpinner";

// This page is an Instagram redirect target. Its url has to be listed as an OAuth
// redirect URI in the Meta app, next to /apply/instagram, or Instagram refuses the request.
// Meta app 1011075517160526, Instagram, API setup with Instagram business login,
// Business login settings, OAuth redirect URIs. Add the url of every site we open it on:
//   https://dev.schmodel.club/instagram-exchange
//   https://test.schmodel.club/instagram-exchange
const REDIRECT_URI = `${window.location.origin}/instagram-exchange`.replace("http://", "https://");

const PUBLIC_EXCHANGE_TOKEN = gql`
  mutation publicExchangeToken($code: String!, $redirectUri: String!) {
    publicExchangeToken(code: $code, redirectUri: $redirectUri)
  }
`;

// Anyone can open this to let Schmodel read their Instagram. It sends them back here with a code.
// dev:  https://www.instagram.com/oauth/authorize?client_id=1011075517160526&redirect_uri=https%3A%2F%2Fdev.schmodel.club%2Finstagram-exchange&response_type=code&scope=instagram_business_basic
// test: https://www.instagram.com/oauth/authorize?client_id=1011075517160526&redirect_uri=https%3A%2F%2Ftest.schmodel.club%2Finstagram-exchange&response_type=code&scope=instagram_business_basic
const AUTHORIZE_URL = `https://www.instagram.com/oauth/authorize?${qs.stringify({
  client_id: process.env.REACT_APP_SCHMODEL_INSTAGRAM_APP_ID,
  redirect_uri: REDIRECT_URI,
  scope: "instagram_business_basic",
  response_type: "code",
})}`;

const startInstagramOAuth = () => {
  window.location.href = AUTHORIZE_URL;
};

const Tick = () => (
  <svg style={styles.icon} width="56" height="56" viewBox="0 0 56 56" fill="none" stroke="#B08D57" strokeWidth="2" aria-hidden="true">
    <circle cx="28" cy="28" r="26" />
    <path d="m17 29 8 8 15-17" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const Cross = () => (
  <svg style={styles.icon} width="56" height="56" viewBox="0 0 56 56" fill="none" stroke="#FF6B6B" strokeWidth="2" aria-hidden="true">
    <circle cx="28" cy="28" r="26" />
    <path d="M20 20l16 16M36 20L20 36" strokeLinecap="round" />
  </svg>
);

const InstagramExchange = () => {
  const { code, error } = qs.parse(window.location.search);
  const [result, setResult] = useState(null);
  const [exchange] = useMutation(PUBLIC_EXCHANGE_TOKEN);

  useEffect(() => {
    if (!code) return;
    exchange({ variables: { code, redirectUri: REDIRECT_URI } })
      .then(({ data }) => setResult(data.publicExchangeToken ? "ok" : "failed"))
      .catch(() => setResult("failed"));
  }, [code]);

  // Instagram sent the member back without a code, or our exchange did not work.
  const failed = !!error || result === "failed";
  const connecting = !!code && !result && !error;

  return (
    <div style={styles.page}>
      <div style={styles.card}>
        {connecting && (
          <>
            <div style={styles.spinner}><GoldSpinner /></div>
            <p style={styles.lede}>Connecting your Instagram…</p>
          </>
        )}

        {result === "ok" && (
          <>
            <Tick />
            <h1 style={styles.title}>You're connected</h1>
            <p style={styles.lede}>Your Instagram is now connected to Schmodel. You can close this page.</p>
          </>
        )}

        {failed && (
          <>
            <Cross />
            <h1 style={styles.title}>We couldn't connect your Instagram</h1>
            <button type="button" style={styles.button} onClick={startInstagramOAuth}>
              Try again
            </button>
          </>
        )}

        {!code && !error && (
          <>
            <h1 style={styles.title}>Connect your Instagram</h1>
            <p style={styles.lede}>
              We only see what is already public on your profile. It takes about a minute.
            </p>
            <button type="button" style={styles.button} onClick={startInstagramOAuth}>
              Connect Instagram
            </button>
          </>
        )}
      </div>
    </div>
  );
};

export default InstagramExchange;

const styles = {
  page: {
    minHeight: "100vh",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: 24,
    background: "black",
  },
  card: {
    width: "100%",
    maxWidth: 420,
    textAlign: "center",
    color: "#eee",
    fontFamily: "'Helvetica Neue', Helvetica, Arial, sans-serif",
  },
  icon: { display: "block", margin: "0 auto" },
  spinner: { display: "flex", justifyContent: "center", marginBottom: 24 },
  title: { fontSize: 26, fontWeight: 500, color: "#fff", margin: "24px 0 12px" },
  lede: { fontSize: 15, lineHeight: 1.6, color: "#bbb", margin: 0 },
  button: {
    marginTop: 28,
    padding: "14px 28px",
    fontSize: 15,
    letterSpacing: 0.5,
    cursor: "pointer",
    background: "#B08D57",
    color: "black",
    border: "none",
    borderRadius: 2,
  },
};
